import { useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

// Color palette for the public site. Values are hex strings so they can be
// edited with a native <input type="color"> in the admin panel.
export interface ColorPalette {
  primary: string;
  primaryLight: string;
  primaryDark: string;
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  accent: string;
  accentLight: string;
  accentDark: string;
}

// Defaults must stay in sync with the :root variables in index.css.
export const defaultPalette: ColorPalette = {
  // Warm taupe/brown primary (replaces the cold anthracite) + warm gold glow +
  // stone base. See index.css :root (keep both in sync).
  primary: '#5a4e42',
  primaryLight: '#7c6e5e',
  primaryDark: '#3e362c',
  secondary: '#c6a15e',
  secondaryLight: '#ddbb84',
  secondaryDark: '#9c7638',
  accent: '#7e7259',
  accentLight: '#9e9179',
  accentDark: '#5e543f',
};

// Maps palette keys to the CSS custom properties consumed by tailwind.config.js.
const cssVarMap: Record<keyof ColorPalette, string> = {
  primary: '--color-primary',
  primaryLight: '--color-primary-light',
  primaryDark: '--color-primary-dark',
  secondary: '--color-secondary',
  secondaryLight: '--color-secondary-light',
  secondaryDark: '--color-secondary-dark',
  accent: '--color-accent',
  accentLight: '--color-accent-light',
  accentDark: '--color-accent-dark',
};

// Converts "#2d5a3d" (or "#abc") to space-separated RGB channels "45 90 61".
// Tailwind reads these channels via rgb(var(--color-x) / <alpha-value>) so that
// opacity utilities keep working. Returns null for invalid input.
export const hexToRgbChannels = (hex: string): string | null => {
  if (!hex) return null;
  const clean = hex.replace('#', '').trim();
  const full = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
};

// Writes the palette onto :root as CSS variables. Missing/invalid keys fall
// back to the defaults so the site is never left without colors.
export const applyPalette = (palette?: Partial<ColorPalette> | null): void => {
  if (typeof document === 'undefined') return;
  const merged: ColorPalette = { ...defaultPalette, ...(palette || {}) };
  const root = document.documentElement;
  (Object.keys(cssVarMap) as (keyof ColorPalette)[]).forEach((key) => {
    const channels = hexToRgbChannels(merged[key]);
    if (channels) {
      root.style.setProperty(cssVarMap[key], channels);
    }
  });
};

// Loads the saved palette from Firestore once and applies it globally.
// Read-only and failure-tolerant: on any error the CSS :root defaults remain.
export const useThemeColors = (): void => {
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'content', 'site-settings'));
        if (active && snap.exists()) {
          applyPalette(snap.data().colors as Partial<ColorPalette> | undefined);
        }
      } catch (err) {
        // Keep the CSS default palette on failure.
        console.error('Error loading theme colors:', err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);
};
